<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const statusOrder = [
  {
    status: 'Selesai',
    color: 'success',
    icon: 'ri-checkbox-circle-line',
    keterangan: 'Transaksi berhasil diproses dan dibayar. Data masuk ke laporan penjualan.',
    bisaVoid: true,
  },
  {
    status: 'Pending',
    color: 'warning',
    icon: 'ri-time-line',
    keterangan: 'Transaksi masih dalam proses (jarang terjadi dalam kondisi normal).',
    bisaVoid: false,
  },
  {
    status: 'Void',
    color: 'error',
    icon: 'ri-close-circle-line',
    keterangan: 'Transaksi dibatalkan oleh pemilik atau manager. Stok bahan baku dikembalikan.',
    bisaVoid: false,
  },
]

const statusShift = [
  {
    status: 'Berjalan',
    color: 'success',
    keterangan: 'Shift sedang aktif. Kasir bisa memproses transaksi.',
  },
  {
    status: 'Ditutup',
    color: 'default',
    keterangan: 'Shift sudah ditutup. Tidak ada transaksi baru yang bisa diproses dalam shift ini.',
  },
]

const faqOrder = [
  {
    q: 'Saya ingin melihat semua transaksi dari kasir tertentu saja. Bisa?',
    a: 'Saat ini filter berdasarkan kasir belum tersedia langsung di halaman daftar order. Namun Anda bisa melihat per-kasir melalui halaman Shift Kasir — klik detail shift kasir tersebut untuk melihat ringkasannya. Laporan penjualan yang lebih detail tersedia di menu Laporan.',
  },
  {
    q: 'Apakah laporan keuangan langsung berubah saat saya void order?',
    a: 'Ya, langsung. Order yang di-void dihapus dari laporan penjualan harian dan tidak masuk ke hitungan omzet. Stok bahan baku juga langsung dikembalikan ke jumlah sebelum transaksi.',
  },
  {
    q: 'Berapa lama data order tersimpan di sistem?',
    a: 'Data order tersimpan permanen di sistem selama toko Anda aktif berlangganan PintuKasir. Tidak ada penghapusan otomatis. Anda bisa mencari transaksi lama kapan saja dengan filter tanggal.',
  },
  {
    q: 'Apakah pelanggan bisa dapat salinan struk digital?',
    a: 'Saat ini struk dicetak fisik dari halaman detail order atau langsung setelah transaksi. Fitur kirim struk via WhatsApp/email belum tersedia di versi ini.',
  },
  {
    q: 'Kenapa ada transaksi yang tidak ada nama pelanggannya?',
    a: 'Nama pelanggan bersifat opsional di POS kasir. Kasir bisa proses transaksi tanpa mengisi nama pelanggan untuk pelanggan anonim. Ini wajar untuk coffee shop dan UMKM yang melayani pembeli walk-in.',
  },
]

const faqShift = [
  {
    q: 'Apakah owner bisa tutup shift kasir yang bukan miliknya?',
    a: 'Ya. Pemilik atau manager yang punya permission dapat menutup shift kasir siapapun dari halaman Shift Kasir. Ini berguna jika kasir lupa tutup shift sendiri sebelum pulang.',
  },
  {
    q: 'Saya lihat shift sudah berhari-hari dan statusnya masih "Berjalan". Apa yang harus dilakukan?',
    a: 'Ini artinya shift belum ditutup. Tutup shift tersebut dari halaman Shift Kasir (klik ikon kunci 🔒 di baris shift yang bersangkutan). Isi kas akhir sesuai kenyataan saat ini. Ke depan, biasakan kasir menutup shift setiap akhir giliran kerja.',
  },
  {
    q: 'Apa itu "Kas Seharusnya" (Expected Cash) dan bagaimana cara menghitungnya?',
    a: 'Kas seharusnya = Kas Awal + Total penjualan tunai selama shift. Contoh: Kas awal Rp 500.000, penjualan tunai Rp 1.200.000, maka kas seharusnya = Rp 1.700.000. Sistem menghitung ini otomatis. Anda tinggal menghitung uang fisik di laci dan membandingkan.',
  },
  {
    q: 'Bagaimana jika ada selisih kas yang besar?',
    a: 'Jangan panik. Pertama, hitung ulang uang di laci — mungkin ada yang terselip. Kedua, cek apakah ada transaksi QRIS atau transfer yang tidak diakui sebagai tunai. Ketiga, isi kolom "Catatan" saat tutup shift dengan penjelasan selisih. Pemilik bisa review dari laporan shift untuk investigasi lebih lanjut.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Penjualan</span>
        </div>
        <RouterLink to="/userguide">
          <VBtn variant="text" prepend-icon="ri-arrow-left-line" size="small" class="mb-4">
            Kembali ke Daftar Modul
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="primary" variant="tonal" size="64" rounded="lg" class="me-4">
            <VIcon icon="ri-receipt-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">
              Penjualan
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Monitor riwayat transaksi, detail order, dan laporan shift kasir
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong>
          Menjelaskan cara memantau semua transaksi yang terjadi, membaca detail order,
          membatalkan transaksi yang salah (void), dan memonitor kinerja kasir melalui riwayat shift.
          Bagian ini lebih banyak digunakan oleh <strong>pemilik dan manager</strong>
          daripada kasir.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Dua Sub-menu -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-map-line" class="me-2" color="primary" />
            Dua Halaman di Menu Penjualan
          </VCardTitle>
          <VCardText>
            <VRow>
              <VCol cols="12" md="6">
                <VCard variant="outlined" class="h-100">
                  <VCardText>
                    <div class="d-flex align-center gap-3 mb-3">
                      <VAvatar color="primary" variant="tonal" size="44" rounded>
                        <VIcon icon="ri-receipt-line" size="24" />
                      </VAvatar>
                      <div>
                        <h6 class="text-h6 font-weight-bold mb-0">Semua Order</h6>
                        <p class="text-caption text-medium-emphasis mb-0">Menu: Penjualan → Semua Order</p>
                      </div>
                    </div>
                    <p class="text-body-2 mb-0">
                      Daftar lengkap semua transaksi yang pernah terjadi di toko Anda.
                      Bisa difilter berdasarkan status (Selesai / Void / Pending) dan
                      rentang tanggal. Cocok untuk review harian atau investigasi transaksi
                      tertentu.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="6">
                <VCard variant="outlined" class="h-100">
                  <VCardText>
                    <div class="d-flex align-center gap-3 mb-3">
                      <VAvatar color="warning" variant="tonal" size="44" rounded>
                        <VIcon icon="ri-time-line" size="24" />
                      </VAvatar>
                      <div>
                        <h6 class="text-h6 font-weight-bold mb-0">Shift Kasir</h6>
                        <p class="text-caption text-medium-emphasis mb-0">Menu: Penjualan → Shift Kasir</p>
                      </div>
                    </div>
                    <p class="text-body-2 mb-0">
                      Riwayat semua shift kasir: siapa yang buka, kapan, berapa kas awal dan akhir,
                      dan apakah ada selisih. Cocok untuk rekonsiliasi kas harian dan
                      evaluasi kinerja kasir.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- ======================== BAGIAN 1: ORDER ======================== -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-4 d-flex align-center gap-2">
          <VIcon icon="ri-receipt-line" color="primary" />
          Bagian 1: Semua Order
        </h5>
      </VCol>
    </VRow>

    <!-- Status Order -->
    <VRow>
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-information-line" class="me-2" color="info" />
            Memahami Status Order
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Setiap order memiliki status yang menunjukkan kondisi saat ini:
            </p>
            <VTable>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Artinya</th>
                  <th>Bisa Divoid?</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in statusOrder" :key="s.status">
                  <td>
                    <VChip :color="s.color" size="small" variant="tonal">
                      <VIcon :icon="s.icon" size="14" class="me-1" />
                      {{ s.status }}
                    </VChip>
                  </td>
                  <td class="text-body-2">{{ s.keterangan }}</td>
                  <td class="text-body-2">
                    <span v-if="s.bisaVoid" class="text-success font-weight-medium">✅ Ya</span>
                    <span v-else class="text-medium-emphasis">❌ Tidak</span>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Filter Order -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-filter-line" class="me-2" color="primary" />
            Cara Mencari Order Tertentu
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Di halaman <strong>Semua Order</strong>, gunakan filter berikut untuk menemukan transaksi yang Anda cari:
            </p>
            <VTable density="compact">
              <thead>
                <tr>
                  <th style="min-width: 140px">Filter</th>
                  <th>Cara Pakai</th>
                  <th>Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Status</strong></td>
                  <td class="text-body-2">Dropdown pilihan. Pilih "Selesai" untuk lihat transaksi sukses saja.</td>
                  <td class="text-body-2">Selesai / Void / Pending</td>
                </tr>
                <tr>
                  <td><strong>Dari Tanggal</strong></td>
                  <td class="text-body-2">Pilih tanggal awal rentang yang ingin dilihat.</td>
                  <td class="text-body-2">01/06/2025</td>
                </tr>
                <tr>
                  <td><strong>Sampai Tanggal</strong></td>
                  <td class="text-body-2">Pilih tanggal akhir rentang.</td>
                  <td class="text-body-2">30/06/2025</td>
                </tr>
                <tr>
                  <td><strong>Reset Filter</strong></td>
                  <td class="text-body-2">Klik tombol "Reset Filter" untuk tampilkan semua order kembali.</td>
                  <td class="text-body-2">—</td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Detail Order -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-eye-line" class="me-2" color="info" />
            Membaca Detail Order
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Klik nomor order (misal <strong>ORD-20250615-001</strong>) untuk membuka halaman detail.
              Berikut informasi yang tersedia:
            </p>

            <VRow>
              <VCol cols="12" md="6">
                <h6 class="text-subtitle-2 font-weight-bold mb-2">Panel Kiri — Item Pesanan</h6>
                <VList density="compact">
                  <VListItem prepend-icon="ri-checkbox-blank-circle-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      Daftar produk yang dipesan, lengkap dengan jumlah, harga satuan, dan subtotal
                    </VListItemTitle>
                  </VListItem>
                  <VListItem prepend-icon="ri-checkbox-blank-circle-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      Catatan per item (jika kasir mengisi catatan saat transaksi)
                    </VListItemTitle>
                  </VListItem>
                  <VListItem prepend-icon="ri-checkbox-blank-circle-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      Tombol <strong>"Tampilkan HPP"</strong> (khusus yang punya akses Inventory) — untuk lihat biaya bahan baku per item dan estimasi laba kotor transaksi ini
                    </VListItemTitle>
                  </VListItem>
                </VList>
              </VCol>
              <VCol cols="12" md="6">
                <h6 class="text-subtitle-2 font-weight-bold mb-2">Panel Kanan — Ringkasan & Pembayaran</h6>
                <VList density="compact">
                  <VListItem prepend-icon="ri-checkbox-blank-circle-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      Nama pelanggan (jika diisi kasir)
                    </VListItemTitle>
                  </VListItem>
                  <VListItem prepend-icon="ri-checkbox-blank-circle-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      Subtotal, diskon, pajak, dan total transaksi
                    </VListItemTitle>
                  </VListItem>
                  <VListItem prepend-icon="ri-checkbox-blank-circle-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      Rincian metode pembayaran yang digunakan (tunai, QRIS, dll) dan kembalian
                    </VListItemTitle>
                  </VListItem>
                  <VListItem prepend-icon="ri-checkbox-blank-circle-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      Informasi kasir yang memproses transaksi dan waktu transaksi
                    </VListItemTitle>
                  </VListItem>
                </VList>
              </VCol>
            </VRow>

            <VAlert type="success" variant="tonal" class="mt-3 mb-0">
              <strong>💡 Fitur HPP:</strong> Jika Anda mengklik "Tampilkan HPP" di halaman detail order,
              sistem akan menampilkan total biaya bahan baku yang dipakai dan estimasi laba kotor.
              Contoh: Total penjualan Rp 75.000, HPP Rp 28.000, estimasi laba kotor Rp 47.000.
              Fitur ini hanya muncul bagi pengguna yang punya akses ke modul Inventory.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Void Order -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-close-circle-line" class="me-2" color="error" />
            Cara Membatalkan Transaksi (Void Order)
          </VCardTitle>
          <VCardText>
            <VAlert type="warning" variant="tonal" class="mb-4">
              <strong>Void adalah tindakan permanen.</strong>
              Setelah order di-void, tidak bisa dikembalikan ke status "Selesai".
              Pastikan Anda yakin sebelum melakukan void.
            </VAlert>

            <h6 class="text-subtitle-2 font-weight-bold mb-2">Kapan Perlu Void Order?</h6>
            <VList density="compact" class="mb-4">
              <VListItem prepend-icon="ri-arrow-right-s-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  Kasir salah input produk (beda menu yang dipesan dengan yang di-input)
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-arrow-right-s-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  Pelanggan membatalkan pesanan setelah transaksi selesai
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-arrow-right-s-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  Terjadi kesalahan harga karena produk belum diupdate di sistem
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-arrow-right-s-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  Pembayaran ganda (pelanggan bayar dua kali untuk transaksi yang sama)
                </VListItemTitle>
              </VListItem>
            </VList>

            <h6 class="text-subtitle-2 font-weight-bold mb-2">Langkah-langkah Void Order:</h6>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">
                Buka <strong>Penjualan → Semua Order</strong>
              </li>
              <li class="mb-2">
                Temukan order yang ingin di-void (gunakan filter tanggal jika perlu).
                Order harus berstatus <VChip size="x-small" color="success" variant="tonal">Selesai</VChip>
              </li>
              <li class="mb-2">
                Klik ikon <strong>❌</strong> di kolom Aksi, atau buka detail order dan klik tombol
                <strong>"Void Order Ini"</strong> (tombol merah di bawah panel kanan)
              </li>
              <li class="mb-2">
                Di dialog yang muncul, isi <strong>Alasan Void</strong> secara spesifik.
                Contoh: <em>"Kasir salah pilih menu — harusnya Americano bukan Cappuccino"</em>
              </li>
              <li class="mb-2">
                Klik <strong>"Ya, Void Order Ini"</strong>
              </li>
              <li class="mb-0">
                Status order berubah menjadi <VChip size="x-small" color="error" variant="tonal">Void</VChip> dan stok bahan baku dikembalikan otomatis ✅
              </li>
            </ol>

            <VAlert type="info" variant="tonal" density="compact" class="text-body-2">
              <strong>Catatan Permission:</strong> Hanya pengguna dengan permission
              <strong>"delete Sales"</strong> yang bisa melakukan void. Biasanya hanya pemilik dan manager.
              Kasir biasa tidak bisa void sendiri — harus meminta pemilik.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ Order -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h6 class="text-h6 font-weight-bold mb-3">
          ❓ Pertanyaan Seputar Order
        </h6>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faqOrder" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- ======================== BAGIAN 2: SHIFT ======================== -->
    <VDivider class="my-8" />

    <VRow>
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-4 d-flex align-center gap-2">
          <VIcon icon="ri-time-line" color="warning" />
          Bagian 2: Shift Kasir
        </h5>
      </VCol>
    </VRow>

    <!-- Kolom Tabel Shift -->
    <VRow>
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-table-line" class="me-2" color="warning" />
            Membaca Tabel Riwayat Shift
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Halaman <strong>Penjualan → Shift Kasir</strong> menampilkan tabel berisi semua shift
              yang pernah dibuka di toko Anda. Berikut penjelasan setiap kolom:
            </p>

            <VTable>
              <thead>
                <tr>
                  <th style="min-width: 120px">Kolom</th>
                  <th>Artinya</th>
                  <th>Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Kasir</strong></td>
                  <td class="text-body-2">Nama karyawan yang membuka shift ini</td>
                  <td class="text-body-2">Budi Santoso</td>
                </tr>
                <tr>
                  <td><strong>Status</strong></td>
                  <td class="text-body-2">Apakah shift masih aktif atau sudah ditutup</td>
                  <td>
                    <VChip size="small" color="success" variant="tonal">Berjalan</VChip>
                    /
                    <VChip size="small" color="default" variant="tonal">Ditutup</VChip>
                  </td>
                </tr>
                <tr>
                  <td><strong>Kas Awal</strong></td>
                  <td class="text-body-2">Jumlah uang di laci saat kasir membuka shift</td>
                  <td class="text-body-2">Rp 500.000</td>
                </tr>
                <tr>
                  <td><strong>Kas Akhir</strong></td>
                  <td class="text-body-2">Jumlah uang di laci saat kasir menutup shift (hasil hitung fisik)</td>
                  <td class="text-body-2">Rp 1.750.000</td>
                </tr>
                <tr>
                  <td><strong>Selisih</strong></td>
                  <td class="text-body-2">
                    Perbedaan antara kas akhir dengan kas yang seharusnya ada.
                    <span class="text-success">+</span> = lebih,
                    <span class="text-error">-</span> = kurang,
                    <span class="text-success">0</span> = pas
                  </td>
                  <td class="text-body-2">
                    <span class="text-error">-Rp 15.000</span>
                    atau
                    <span class="text-success">+Rp 5.000</span>
                  </td>
                </tr>
                <tr>
                  <td><strong>Dibuka</strong></td>
                  <td class="text-body-2">Tanggal dan jam kasir membuka shift</td>
                  <td class="text-body-2">15 Jun 2025, 08:15</td>
                </tr>
                <tr>
                  <td><strong>Ditutup</strong></td>
                  <td class="text-body-2">Tanggal dan jam shift ditutup. Kosong (-) jika masih berjalan</td>
                  <td class="text-body-2">15 Jun 2025, 21:45</td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Tutup Shift dari Halaman Ini -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lock-line" class="me-2" color="error" />
            Cara Tutup Shift dari Halaman Ini (oleh Owner/Manager)
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Owner atau manager bisa tutup shift siapapun dari halaman ini — berguna jika kasir
              lupa atau tidak bisa tutup sendiri.
            </p>
            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-2">
                Cari shift yang statusnya <VChip size="x-small" color="success" variant="tonal">Berjalan</VChip>
              </li>
              <li class="mb-2">
                Klik ikon kunci (<strong>🔒</strong>) di kolom Aksi
              </li>
              <li class="mb-2">
                Dialog "Tutup Shift" muncul dengan ringkasan:
                kas awal, estimasi penjualan tunai shift ini, dan total kas seharusnya
              </li>
              <li class="mb-2">
                Isi kolom <strong>"Kas Akhir (Hasil Hitung Fisik)"</strong> dengan uang yang ada di laci
              </li>
              <li class="mb-2">
                Sistem langsung menampilkan estimasi selisih:
                <VChip size="x-small" color="success" variant="tonal">Kas pas ✓</VChip>,
                <VChip size="x-small" color="info" variant="tonal">Lebih +</VChip>, atau
                <VChip size="x-small" color="error" variant="tonal">Kurang -</VChip>
              </li>
              <li class="mb-2">
                Isi <strong>Catatan</strong> jika ada penjelasan (misal: "ada retur tunai Rp 20.000")
              </li>
              <li class="mb-0">
                Klik <strong>"Tutup Shift"</strong>
              </li>
            </ol>

            <VAlert type="info" variant="tonal" class="mb-0">
              <strong>Catatan:</strong> Angka selisih yang ditampilkan sebelum submit adalah
              <em>estimasi</em>. Angka final (resmi) dihitung oleh sistem di server saat
              Anda klik "Tutup Shift", berdasarkan total penjualan tunai yang tercatat di database.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara baca selisih kas -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-calculator-line" class="me-2" color="info" />
            Cara Membaca Laporan Selisih Kas
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Selisih kas adalah salah satu indikator integritas kasir Anda.
              Berikut panduan membacanya:
            </p>

            <VRow>
              <VCol cols="12" md="4">
                <VCard color="success" variant="tonal" class="h-100">
                  <VCardText class="text-center pa-4">
                    <VIcon icon="ri-checkbox-circle-line" size="40" color="success" class="mb-2" />
                    <h6 class="text-h6 font-weight-bold mb-1">Selisih = 0</h6>
                    <p class="text-body-2 mb-0">
                      <strong>Kas pas.</strong> Semua transaksi tunai tercatat dengan benar
                      dan uang di laci sesuai ekspektasi. Kondisi ideal.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="4">
                <VCard color="info" variant="tonal" class="h-100">
                  <VCardText class="text-center pa-4">
                    <VIcon icon="ri-arrow-up-circle-line" size="40" color="info" class="mb-2" />
                    <h6 class="text-h6 font-weight-bold mb-1">Selisih +</h6>
                    <p class="text-body-2 mb-0">
                      <strong>Kas lebih.</strong> Bisa karena ada pembayaran yang tidak tercatat,
                      atau kasir hitung kas awal terlalu rendah. Perlu investigasi kecil.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="4">
                <VCard color="error" variant="tonal" class="h-100">
                  <VCardText class="text-center pa-4">
                    <VIcon icon="ri-arrow-down-circle-line" size="40" color="error" class="mb-2" />
                    <h6 class="text-h6 font-weight-bold mb-1">Selisih −</h6>
                    <p class="text-body-2 mb-0">
                      <strong>Kas kurang.</strong> Ada uang yang seharusnya ada tapi tidak ada.
                      Perlu dicek apakah ada kesalahan hitung, pengembalian yang tidak tercatat,
                      atau hal lain.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>

            <VAlert type="warning" variant="tonal" class="mt-4 mb-0">
              <strong>Tip:</strong> Selisih kecil (Rp 1.000–5.000) bisa terjadi karena pembulatan kembalian.
              Selisih besar yang berulang perlu diinvestigasi lebih serius.
              Klik ikon mata (👁️) di kolom Aksi untuk lihat detail shift dan catatan kasir.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ Shift -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h6 class="text-h6 font-weight-bold mb-3">
          ❓ Pertanyaan Seputar Shift Kasir
        </h6>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faqShift" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="primary" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Best Practices Pengelolaan Shift & Penjualan
          </VCardTitle>
          <VCardText>
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Buat jadwal tutup shift yang rutin.</strong>
                  Misalnya setiap ganti shift (pagi ke siang, siang ke malam)
                  atau setiap tutup toko. Jangan biarkan shift terbuka lebih dari satu hari.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Cek halaman Penjualan setiap hari.</strong>
                  Pemilik disarankan review daftar order hari ini di akhir hari untuk
                  memastikan tidak ada anomali (transaksi nilai sangat besar/kecil yang tidak wajar).
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Dokumentasikan alasan void dengan spesifik.</strong>
                  Alasan yang jelas ("Salah input – harusnya Es Kopi bukan Hot Kopi")
                  membantu audit internal di kemudian hari.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Gunakan filter tanggal untuk rekap mingguan/bulanan.</strong>
                  Pilih tanggal 1–31 bulan ini untuk lihat semua order bulan ini sekaligus
                  sebelum buat laporan.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Halaman Terkait -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'POS Kasir', sub: 'Panduan melayani transaksi langsung', icon: 'ri-shopping-cart-line', color: 'success', to: '/userguide/operasional/pos-kasir' },
              { title: 'Laporan Penjualan', sub: 'Analisa omzet, produk terlaris, dll', icon: 'ri-bar-chart-line', color: 'secondary', to: '/userguide/laporan/penjualan' },
              { title: 'User & Role', sub: 'Atur permission void untuk kasir vs manager', icon: 'ri-user-settings-line', color: 'warning', to: '/userguide/sistem/user-role' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
